import { ref } from 'vue'
import { supabase } from '../supabaseClient'
import { getAuthUserShared } from './useAuthUser.js'

/** 指定 ms で必ず reject する。getUser 等のネットワーク待ちが永久にハングするのを防ぐ */
function withTimeout(promise, ms, message) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message || 'timeout')), ms)
    promise.then(
      (v) => { clearTimeout(timer); resolve(v) },
      (e) => { clearTimeout(timer); reject(e) }
    )
  })
}

function isSessionMissingError(e) {
  if (!e) return false
  const name = e.name || e.code
  const msg = String(e.message || '')
  return (
    name === 'AuthSessionMissingError' ||
    msg.includes('Auth session missing') ||
    msg.includes('session missing')
  )
}

// 認証済みユーザー / プロフィール / 最初の子ども情報をまとめて管理する composable
// - 1セッション中はキャッシュし、複数コンポーネントから共有する

const currentUser = ref(null)
const profile = ref(null)
const firstLearner = ref(null)
const loading = ref(false)
const error = ref(null)
let initialized = false

export function useAuthProfile() {
  // 認証 + プロフィール + 1人目の learner をまとめて読み込む
  const ensureAuthProfileLoaded = async () => {
    if (initialized || loading.value) return

    loading.value = true
    error.value = null

    try {
      // 1. Supabaseユーザーを取得
      // 未ログイン・または localStorage からのセッション復元前だと AuthSessionMissingError になり得る（想定内）
      // ページを開いた直後はガードと同時に呼ばれるため、確認を共有する（指示書 A3）
      const { data: { user }, error: userError } = await withTimeout(
        getAuthUserShared(),
        8000,
        'ensureAuthProfileLoaded: getUser timeout'
      )
      if (userError) {
        if (isSessionMissingError(userError)) {
          currentUser.value = null
          profile.value = null
          firstLearner.value = null
          return
        }
        throw userError
      }
      if (!user) {
        currentUser.value = null
        profile.value = null
        firstLearner.value = null
        return
      }

      currentUser.value = user

      // 2. users_phase2 / learners を並列取得
      const [profileResult, learnerResult] = await Promise.all([
        supabase
          .from('users_phase2')
          .select('subscription_status, bypass_subscription, role, full_name, mic_check_completed, current_day')
          .eq('id', user.id)
          .maybeSingle(),
        supabase
          .from('learners')
          .select('id, name, name_kana, birth_date, current_day, type, parent_id')
          .eq('parent_id', user.id)
          .eq('type', 'child')
          .order('created_at', { ascending: true })
          .limit(1)
      ])

      if (profileResult.error) throw profileResult.error
      if (learnerResult.error) throw learnerResult.error

      profile.value = profileResult.data || null
      firstLearner.value = (learnerResult.data && learnerResult.data[0]) || null

      initialized = true
    } catch (e) {
      if (isSessionMissingError(e)) {
        currentUser.value = null
        profile.value = null
        firstLearner.value = null
        return
      }
      console.error('useAuthProfile.ensureAuthProfileLoaded error:', e)
      error.value = e
      // 失敗しても次回再試行できるようにフラグは立てない
    } finally {
      loading.value = false
    }
  }

  // 強制的に初期状態へ戻す（ログアウト時など）
  const resetAuthProfile = () => {
    currentUser.value = null
    profile.value = null
    firstLearner.value = null
    loading.value = false
    error.value = null
    initialized = false
  }

  return {
    currentUser,
    profile,
    firstLearner,
    loading,
    error,
    ensureAuthProfileLoaded,
    resetAuthProfile
  }
}

